import { windowEvents } from '@/util/interaction'
import type { Unsubscribe } from '@amazon/vinyl-util'
import { getDeviceConfig } from '@/device/deviceConfig'

export type KeyBinding = string | readonly string[]

/**
 * A device's key bindings, matched against `KeyboardEvent.code` or `key` (TV
 * remotes often report only `key`).
 */
export interface KeyMap {
    readonly down: KeyBinding
    readonly left: KeyBinding
    readonly right: KeyBinding
    readonly up: KeyBinding

    readonly back: KeyBinding
    readonly enter: KeyBinding
    readonly escape: KeyBinding

    readonly playPause: KeyBinding
    readonly play: KeyBinding
    readonly pause: KeyBinding
    readonly stop: KeyBinding
    readonly next: KeyBinding
    readonly previous: KeyBinding
    readonly fastForward: KeyBinding
    readonly rewind: KeyBinding
}

/**
 * Returns true if the keyboard event matches the given key binding.
 */
export function matchesKey(event: KeyboardEvent, binding: KeyBinding): boolean {
    const type = typeof binding
    if (type === 'string') {
        return event.code === binding || event.key === binding
    } else if (type === 'object' && Array.isArray(binding)) {
        return binding.includes(event.code) || binding.includes(event.key)
    } else {
        return false
    }
}

/**
 * Returns a registration for a key binding.
 *
 * @param key The key binding to match.
 * @param callback Called when a keyboard event with the given key has occurred.
 * @return Returns an Unsubscribe, to remove the listener.
 */
export function onKey(key: keyof KeyMap, callback: (event: KeyboardEvent) => void): Unsubscribe {
    const keyBinding = getDeviceConfig().keyMap[key]
    return windowEvents.on('keydown', (event) => {
        if (matchesKey(event, keyBinding)) callback(event)
    })
}
